import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Table, Modal, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../../../services/ProductsService';
import { getRecipes } from '../../../services/RecipesService';
import { toCents, splitPrice, formatCents, priceFields } from '../../../utils/productPrice';
import { SALES_STATUSES, ProductStatusBadge, ProductStatusChips, countByStatus, matchesStatusFilter } from './ProductStatus';

const BTW_RATES = [9, 21];
const SKU_MAX_LENGTH = 100;
const SKU_PATTERN = /^[A-Z0-9]+(-[A-Z0-9]+)*$/;

// Returns an i18n key for the first SKU problem, or '' when fine. Mirrors backend utils/sku.py.
function skuErrorKey(sku, status, isDelivery) {
    const v = (sku || '').trim();
    if (!v) return (status === 'active' && !isDelivery) ? 'products.sku.required' : '';
    if (v.length > SKU_MAX_LENGTH) return 'products.sku.tooLong';
    if (!SKU_PATTERN.test(v)) return 'products.sku.invalid';
    return '';
}

const EMPTY_FORM = {
    recipe_id: '',
    recipe_quantity: '1',
    name_nl: '',
    name_en: '',
    sku: '',
    btw_rate: '9',
    price_excl: '',
    price_incl: '',
    price_basis: 'incl',
    traceable: false,
    is_delivery: false,
    sales_status: 'rd',
};

export default function Products() {
    const { t, i18n } = useTranslation();
    const [products, setProducts] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewFilter, setViewFilter] = useState('all');
    const [form, setForm] = useState(EMPTY_FORM);
    const [skuTouched, setSkuTouched] = useState(false);
    const [skuServerKey, setSkuServerKey] = useState('');

    const filteredProducts = useMemo(() => {
        let list = products;
        list = list.filter(p => matchesStatusFilter(p, viewFilter));
        const q = (searchQuery || '').trim().toLowerCase();
        if (!q) return list;
        return list.filter((p) => {
            const name = (p.name || '').toLowerCase();
            const recipeName = (recipes.find(r => r.id === p.recipe_id)?.name || '').toLowerCase();
            const sku = (p.sku || '').toLowerCase();
            return name.includes(q) || recipeName.includes(q) || sku.includes(q);
        });
    }, [products, recipes, searchQuery, viewFilter]);

    const statusCounts = useMemo(() => countByStatus(products), [products]);

    const recipeOptions = useMemo(
        () =>
            recipes.map((r) => {
                const costPer1 = r.cost_per_unit ?? (r.total_cost / (r.yield_amount || 1));
                return {
                    value: r.id,
                    label: `${r.name} (cost per 1: ${(costPer1 || 0).toFixed(2)})`,
                };
            }),
        [recipes]
    );
    const recipeSelectValue = useMemo(
        () => recipeOptions.find((o) => o.value === parseInt(form.recipe_id, 10)) || null,
        [recipeOptions, form.recipe_id]
    );
    const filterRecipeOption = (option, rawInput) => {
        const q = (rawInput || '').trim().toLowerCase();
        if (!q) return true;
        const tokens = q.split(/\s+/).filter(Boolean);
        const label = (option.label || '').toLowerCase();
        return tokens.every((tok) => label.includes(tok));
    };

    const load = async () => {
        try {
            setLoading(true);
            const [prodsRes, recipesRes] = await Promise.all([getProducts(), getRecipes()]);
            setProducts(prodsRes.data || []);
            setRecipes(recipesRes.data || []);
        } catch {
            toast.error(t('products.errors.load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleOpenAdd = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setSkuTouched(false);
        setSkuServerKey('');
        setShowModal(true);
    };

    const handleOpenEdit = (p) => {
        setEditingId(p.id);
        setSkuTouched(false);
        setSkuServerKey('');
        const rate = String(p.btw_rate || 9);
        // Both values come from the server exactly as stored (incl is the customer-facing price); nothing is recomputed here
        const incl = p.price_incl_tax != null ? formatCents(toCents(String(p.price_incl_tax))) : '';
        const excl = p.price != null ? formatCents(toCents(String(p.price))) : '';
        setForm({
            recipe_id: p.recipe_id || '',
            recipe_quantity: p.recipe_quantity != null ? String(p.recipe_quantity) : '1',
            name_nl: p.name_nl || p.name || '',
            name_en: p.name_en || '',
            sku: p.sku || '',
            btw_rate: rate,
            price_excl: excl,
            price_incl: incl,
            price_basis: p.price_basis || 'incl',
            traceable: Boolean(p.traceable),
            is_delivery: Boolean(p.is_delivery),
            sales_status: p.sales_status || 'rd',
        });
        setShowModal(true);
    };

    // The field the user typed (price_basis) stays as-is; the other one is derived from it with the new rate
    const withPrice = (f, patch) => {
        const next = { ...f, ...patch };
        const typed = next.price_basis === 'excl' ? next.price_excl : next.price_incl;
        const fields = priceFields(typed, parseFloat(next.btw_rate) || 9, next.price_basis);
        if (!fields) return next;
        return next.price_basis === 'excl' ? { ...next, price_incl: fields.incl } : { ...next, price_excl: fields.excl };
    };

    // When recipe selection changes: the rate follows the recipe, the typed price stays
    const handleRecipeChange = (opt) => {
        const id = opt?.value ? String(opt.value) : '';
        const r = recipes.find((x) => x.id === parseInt(id, 10));
        const autoRate = id ? '9' : '21';
        setForm((f) => withPrice(f, { recipe_id: id, name_nl: r?.name || f.name_nl, btw_rate: autoRate }));
    };

    // When BTW rate changes: the typed price stays, the other is re-derived
    const handleBtwChange = (newRate) => setForm((f) => withPrice(f, { btw_rate: newRate }));

    // Incl. BTW typed: stored exactly as typed, excl. is derived
    const handlePriceInclChange = (val) => setForm((f) => {
        const next = { ...f, price_incl: val, price_basis: 'incl' };
        const fields = priceFields(val, parseFloat(f.btw_rate) || 9, 'incl');
        return { ...next, price_excl: fields ? fields.excl : '' };
    });

    // Excl. BTW typed: stored exactly as typed, incl. is derived
    const handlePriceExclChange = (val) => setForm((f) => {
        const next = { ...f, price_excl: val, price_basis: 'excl' };
        const fields = priceFields(val, parseFloat(f.btw_rate) || 9, 'excl');
        return { ...next, price_incl: fields ? fields.incl : '' };
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!editingId && !form.recipe_id && !form.name_nl.trim()) {
            toast.warning(t('products.errors.nameRequired'));
            return;
        }
        const typedPrice = form.price_basis === 'excl' ? form.price_excl : form.price_incl;
        if (toCents(typedPrice) === null) {
            toast.warning(t('products.errors.validPrice'));
            return;
        }
        const qty = parseFloat(form.recipe_quantity) || 1;
        if (qty <= 0) {
            toast.warning(t('products.errors.validQuantity'));
            return;
        }
        const btw = parseFloat(form.btw_rate) || 9;
        if (skuErrorKey(form.sku, form.sales_status, form.is_delivery)) {
            setSkuTouched(true);
            return;
        }
        try {
            if (editingId) {
                await updateProduct(editingId, {
                    name_nl: form.name_nl.trim() || undefined,
                    name_en: form.name_en.trim() || '',
                    sku: form.sku.trim(),
                    ...(form.price_basis === 'excl' ? { price: typedPrice } : { price_incl: typedPrice }),
                    recipe_quantity: qty,
                    btw_rate: btw,
                    traceable: form.traceable,
                    is_delivery: form.is_delivery,
                    ...(form.is_delivery ? {} : { sales_status: form.sales_status }),
                });
                toast.success(t('products.success.updated'));
            } else {
                await createProduct({
                    recipe_id: form.recipe_id ? parseInt(form.recipe_id) : undefined,
                    recipe_quantity: qty,
                    name_nl: form.name_nl.trim() || undefined,
                    name_en: form.name_en.trim() || '',
                    sku: form.sku.trim(),
                    ...(form.price_basis === 'excl' ? { price: typedPrice } : { price_incl: typedPrice }),
                    btw_rate: btw,
                    traceable: form.traceable,
                    is_delivery: form.is_delivery,
                    ...(form.is_delivery ? {} : { sales_status: form.sales_status }),
                });
                toast.success(t('products.success.added'));
            }
            setShowModal(false);
            load();
        } catch (err) {
            const key = err.response?.data?.error_key;
            if (key && key.startsWith('products.sku.')) {
                setSkuServerKey(key);
                setSkuTouched(true);
                toast.error(t(key));
            } else {
                toast.error(err.response?.data?.error || t('products.errors.save'));
            }
        }
    };

    const handleDelete = async (p) => {
        const res = await Swal.fire({
            title: t('products.deleteTitle'),
            text: t('products.deleteText', { name: p.name }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: `${t('common.yes')}, ${t('common.delete').toLowerCase()}`,
        });
        if (res.isConfirmed) {
            try {
                await deleteProduct(p.id);
                toast.success(t('products.success.deleted'));
                load();
            } catch {
                toast.error(t('products.errors.delete'));
            }
        }
    };

    // Preview: the typed price is shown exactly as typed; the other values come from the shared calculation
    const previewRate = parseFloat(form.btw_rate) || 9;
    const previewTyped = toCents(form.price_basis === 'excl' ? form.price_excl : form.price_incl) ?? 0;
    const previewParts = splitPrice(previewTyped, previewRate, form.price_basis);
    const previewExcl = previewParts.excl / 100;
    const previewBtw = previewParts.vat / 100;
    const previewIncl = previewParts.incl / 100;

    const skuRequired = form.sales_status === 'active' && !form.is_delivery;
    const skuKey = skuErrorKey(form.sku, form.sales_status, form.is_delivery) || skuServerKey;
    const showSkuError = Boolean(skuKey) && (skuTouched || skuServerKey || (form.sku && skuKey !== 'products.sku.required'));

    const displayName = (p) => (i18n.language.startsWith('en') && p.name_en) ? p.name_en : (p.name_nl || p.name);

    // Cost preview (only when recipe is selected)
    const selectedRecipe = recipes.find(x => x.id === parseInt(form.recipe_id, 10));
    const costPer1 = selectedRecipe
        ? (selectedRecipe.cost_per_unit ?? (selectedRecipe.total_cost / (selectedRecipe.yield_amount || 1)))
        : 0;
    const costVal = costPer1 * (parseFloat(form.recipe_quantity) || 1);
    const profitVal = previewExcl - costVal;
    const marginPct = previewExcl > 0 ? (profitVal / previewExcl) * 100 : 0;
    const markupPct = costVal > 0 ? (profitVal / costVal) * 100 : null;

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div>
                                <h4 className="heading mb-0">{t('products.title')}</h4>
                                <span className="text-muted">{t('products.subtitle')}</span>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="product-filter-row">
                                <ProductStatusChips value={viewFilter} onChange={setViewFilter} counts={statusCounts} includeDelivery />
                                <div className="input-group products-search-bar">
                                    <span className="input-group-text bg-light">
                                        <i className="fa-solid fa-magnifying-glass text-muted" />
                                    </span>
                                    <Form.Control
                                        type="search"
                                        placeholder={t('products.searchPlaceholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        aria-label={t('products.searchAria')}
                                    />
                                    {searchQuery && (
                                        <button type="button" className="btn btn-light" onClick={() => setSearchQuery('')} title={t('common.clearSearch')} aria-label={t('common.clearSearch')}>
                                            <i className="fa-solid fa-xmark text-muted" />
                                        </button>
                                    )}
                                </div>
                                <Button variant="primary" onClick={handleOpenAdd} className="mobile-toolbar-add-btn">
                                    <i className="fa-solid fa-plus me-1" /> {t('products.addProduct')}
                                </Button>
                            </div>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('common.loading')}</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {searchQuery && products.length > 0 && (
                                        <div className="mb-2">
                                            <small className="text-muted">
                                                {t('products.showing', { shown: filteredProducts.length, total: products.length })}
                                                <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => setSearchQuery('')}>
                                                    {t('common.clearSearch')}
                                                </button>
                                            </small>
                                        </div>
                                    )}
                                    <Table responsive hover className="products-main-table">
                                        <thead>
                                            <tr>
                                                <th>{t('products.columns.product')}</th>
                                                <th>{t('products.columns.sku')}</th>
                                                <th>{t('products.columns.recipe')}</th>
                                                <th className="text-center d-none d-xl-table-cell">{t('products.columns.qty')}</th>
                                                <th className="text-center d-none d-xl-table-cell">{t('products.columns.priceExcl')}</th>
                                                <th className="text-center d-none d-xl-table-cell">{t('products.columns.tax')}</th>
                                                <th className="text-center">{t('products.columns.priceIncl')}</th>
                                                <th className="text-center">{t('products.columns.cost')}</th>
                                                <th className="text-center">{t('products.columns.profit')}</th>
                                                <th className="text-center">{t('products.columns.profitMargin')}</th>
                                                <th className="text-center">{t('products.columns.markup')}</th>
                                                <th className="text-center">{t('products.columns.traceable')}</th>
                                                <th className="text-end">{t('products.columns.actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.length === 0 ? (
                                                <tr><td colSpan="13" className="text-center py-5 text-muted">{t('products.noProductsYet')}</td></tr>
                                            ) : filteredProducts.length === 0 ? (
                                                <tr><td colSpan="13" className="text-center py-5 text-muted">{t('products.noProductsMatch', { query: searchQuery })}</td></tr>
                                            ) : (
                                                filteredProducts.map((p) => (
                                                    (() => {
                                                        const priceExcl = Number(p.price ?? 0);
                                                        const costPerUnit = Number(p.cost_per_unit ?? p.cost ?? 0);
                                                        const profitExcl = p.profit_excl != null
                                                            ? Number(p.profit_excl)
                                                            : priceExcl - costPerUnit;
                                                        const margin = p.profit_margin != null
                                                            ? Number(p.profit_margin)
                                                            : (priceExcl > 0 ? (profitExcl / priceExcl) * 100 : null);
                                                        const markup = p.markup != null
                                                            ? Number(p.markup)
                                                            : (costPerUnit > 0 ? (profitExcl / costPerUnit) * 100 : null);
                                                        return (
                                                    <tr key={p.id}>
                                                        <td>
                                            <strong>{displayName(p)}</strong>
                                            {p.is_delivery && (
                                                <span className="badge ms-2" style={{ fontSize: 10, verticalAlign: 'middle', background: '#0dcaf0', color: '#fff' }}>
                                                    {t('products.isDelivery')}
                                                </span>
                                            )}
                                            {!p.is_delivery && <ProductStatusBadge status={p.sales_status} />}
                                        </td>
                                                        <td className="product-sku-cell">{p.sku || <span className="text-muted">—</span>}</td>
                                                        <td>{recipes.find(r => r.id === p.recipe_id)?.name || <span className="text-muted">—</span>}</td>
                                                        <td className="text-center d-none d-xl-table-cell">{p.recipe_quantity != null ? Number(p.recipe_quantity) : 1}</td>
                                                        <td className="text-center d-none d-xl-table-cell">€{p.price?.toFixed(2)}</td>
                                                        <td className="text-center d-none d-xl-table-cell">
                                                            <span className="badge bg-light text-dark border">{p.btw_rate ?? 9}%</span>
                                                        </td>
                                                        <td className="text-center">€{p.price_incl_tax?.toFixed(2)}</td>
                                                        <td className="text-center">€{costPerUnit.toFixed(2)}</td>
                                                        <td className="text-center">
                                                            <span className={profitExcl >= 0 ? 'text-success' : 'text-danger'}>
                                                                €{profitExcl.toFixed(2)}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            {margin != null ? (
                                                                <span className={margin >= 40 ? 'text-success' : 'text-danger'}>{margin.toFixed(1)}%</span>
                                                            ) : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td className="text-center">
                                                            {markup != null ? (
                                                                <span className={markup >= 0 ? 'text-success' : 'text-danger'}>{markup.toFixed(1)}%</span>
                                                            ) : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td className="text-center">
                                                            <Form.Check
                                                                type="switch"
                                                                id={`traceable-${p.id}`}
                                                                checked={Boolean(p.traceable)}
                                                                onChange={async () => {
                                                                    try {
                                                                        await updateProduct(p.id, { traceable: !p.traceable });
                                                                        load();
                                                                    } catch { toast.error(t('products.errors.update')); }
                                                                }}
                                                                label={p.traceable ? t('common.yes') : t('common.no')}
                                                            />
                                                        </td>
                                                        <td className="text-end">
                                                            <Button variant="light" size="sm" className="me-1" onClick={() => handleOpenEdit(p)}>
                                                                <i className="fa-solid fa-pen text-primary" />
                                                            </Button>
                                                            <Button variant="light" size="sm" className="text-danger" onClick={() => handleDelete(p)}>
                                                                <i className="fa-solid fa-trash" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                        );
                                                    })()
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingId ? t('products.editProduct') : t('products.addProduct')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {/* Recipe (optional — food products only) */}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('products.recipeLabel')}</Form.Label>
                            <Select
                                classNamePrefix="react-select"
                                placeholder={t('products.selectRecipe')}
                                isClearable
                                isSearchable
                                isDisabled={!!editingId}
                                options={recipeOptions}
                                value={recipeSelectValue}
                                onChange={handleRecipeChange}
                                filterOption={filterRecipeOption}
                            />
                            <Form.Text className="text-muted">{t('products.recipeHelp')}</Form.Text>
                        </Form.Group>

                        {/* Recipe quantity */}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('products.recipeQuantity')}</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={form.recipe_quantity}
                                onChange={(e) => setForm(f => ({ ...f, recipe_quantity: e.target.value }))}
                                placeholder="e.g. 0.5 or 2"
                            />
                            <Form.Text className="text-muted">{t('products.recipeQuantityHelp')}</Form.Text>
                        </Form.Group>

                        {/* Product name (NL) */}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('products.productNameNl')}</Form.Label>
                            <Form.Control
                                value={form.name_nl}
                                onChange={(e) => setForm(f => ({ ...f, name_nl: e.target.value }))}
                                placeholder={t('products.productNameNlPlaceholder')}
                            />
                        </Form.Group>

                        {/* Product name (EN) */}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('products.productNameEn')}</Form.Label>
                            <Form.Control
                                value={form.name_en}
                                onChange={(e) => setForm(f => ({ ...f, name_en: e.target.value }))}
                                placeholder={t('products.productNameEnPlaceholder')}
                            />
                        </Form.Group>

                        {/* SKU */}
                        <Form.Group className="mb-3">
                            <Form.Label>
                                {t('products.sku.label')}
                                {skuRequired && <span className="text-danger ms-1" data-testid="sku-required-marker">*</span>}
                            </Form.Label>
                            <Form.Control
                                id="sku"
                                className="product-sku-input"
                                value={form.sku}
                                maxLength={SKU_MAX_LENGTH + 20}
                                autoCapitalize="characters"
                                isInvalid={showSkuError}
                                onChange={(e) => { setSkuServerKey(''); setForm(f => ({ ...f, sku: e.target.value.toUpperCase() })); }}
                                onBlur={() => setSkuTouched(true)}
                                placeholder="HAW-LIGHT-MINI"
                            />
                            <Form.Control.Feedback type="invalid">{skuKey ? t(skuKey) : ''}</Form.Control.Feedback>
                            <Form.Text className="text-muted">{t('products.sku.note')}</Form.Text>
                        </Form.Group>

                        {/* BTW rate */}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('products.btwRate')}</Form.Label>
                            <div className="d-flex gap-2">
                                {BTW_RATES.map((rate) => (
                                    <Button
                                        key={rate}
                                        type="button"
                                        variant={parseFloat(form.btw_rate) === rate ? 'primary' : 'outline-secondary'}
                                        size="sm"
                                        style={{ minWidth: 56 }}
                                        onClick={() => handleBtwChange(String(rate))}
                                    >
                                        {rate}%
                                    </Button>
                                ))}
                            </div>
                            <Form.Text className="text-muted">{t('products.btwRateHelp')}</Form.Text>
                        </Form.Group>

                        {/* Price fields — incl. BTW is the primary entry field */}
                        <Row className="mb-2">
                            <Col>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">
                                        {t('products.priceInclBtw')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form.price_incl}
                                        onChange={(e) => handlePriceInclChange(e.target.value)}
                                        required
                                        placeholder="0.00"
                                        autoFocus={!editingId}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group>
                                    <Form.Label className="text-muted">{t('products.priceExclBtw')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form.price_excl}
                                        onChange={(e) => handlePriceExclChange(e.target.value)}
                                        placeholder="0.00"
                                        style={{ background: '#f8f9fa' }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Price preview line */}
                        {previewExcl > 0 && (
                            <div className="mb-3 px-3 py-2 rounded small" style={{ background: '#f0f4ff', border: '1px solid #c7d7fd' }}>
                                {t('products.priceExclBtw').replace(' *', '').replace('*', '')}: <strong>€{previewExcl.toFixed(2)}</strong>
                                {' | '}{t('products.btwRate')} {previewRate}%: <strong>€{previewBtw.toFixed(2)}</strong>
                                {' | '}{t('products.priceInclBtw')}: <strong>€{previewIncl.toFixed(2)}</strong>
                            </div>
                        )}

                        {/* Cost/profit preview when recipe selected */}
                        {selectedRecipe && previewExcl > 0 && (
                            <div className="mb-3 p-2 rounded bg-light small">
                                <strong>{t('products.preview')}</strong>{' '}
                                {t('products.cost')} = €{costVal.toFixed(2)}
                                {' | '}{t('products.profit')} = <span className={profitVal >= 0 ? 'text-success' : 'text-danger'}>€{profitVal.toFixed(2)}</span>
                                {' | '}{t('products.netProfitMargin')} = <span className={profitVal >= 0 ? 'text-success' : 'text-danger'}>{marginPct.toFixed(1)}%</span>
                                {' | '}{t('products.markup')} = {markupPct != null ? (
                                    <span className={markupPct >= 0 ? 'text-success' : 'text-danger'}>{markupPct.toFixed(1)}%</span>
                                ) : <span className="text-muted">—</span>}
                            </div>
                        )}

                        {/* Traceable toggle */}
                        <Form.Group className="mb-2">
                            <Form.Check
                                type="switch"
                                id="traceable"
                                label={t('products.traceable')}
                                checked={form.traceable}
                                onChange={(e) => setForm(f => ({ ...f, traceable: e.target.checked }))}
                            />
                            <Form.Text className="text-muted">{t('products.traceableHelp')}</Form.Text>
                        </Form.Group>

                        {/* Delivery product toggle */}
                        <Form.Group className="mb-2">
                            <Form.Check
                                type="switch"
                                id="is_delivery"
                                label={t('products.isDelivery')}
                                checked={form.is_delivery}
                                onChange={(e) => setForm(f => ({ ...f, is_delivery: e.target.checked }))}
                            />
                            <Form.Text className="text-muted">{t('products.isDeliveryHelp')}</Form.Text>
                        </Form.Group>

                        {/* Sales status (not for delivery products) */}
                        {!form.is_delivery && (
                            <Form.Group className="mb-1">
                                <Form.Label>{t('products.statusLabel')}</Form.Label>
                                <Form.Select
                                    id="sales_status"
                                    value={form.sales_status}
                                    onChange={(e) => setForm(f => ({ ...f, sales_status: e.target.value }))}
                                >
                                    {SALES_STATUSES.map((st) => (
                                        <option key={st} value={st}>{t(`products.status.${st}`)}</option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">{t('products.statusHelp')}</Form.Text>
                            </Form.Group>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</Button>
                        <Button variant="primary" type="submit">{t('common.save')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}
